import { useTheme } from '@mui/material';

import { ActiveStatus, JobStatusByNumber, roleStatusByValue } from 'utils/typedEnums';
import { activeStatusByValue } from 'utils/enums';
import { IndividualHiringRequestAction } from '../types';
import {
  FinalRecommendationDataReturned,
  HiringRequestScorecard,
  ReadableFinalRecommendation,
} from '../types/feedbackDetailTypes';
import {
  CandidateFeedbackFinalRecommendation,
  ConnectHiringRequestAction,
  LongCandidateFeedbackFinalRecommendation,
} from '../enums';
import moment from 'moment';
import { FieldValues, useFormContext, UseFormSetValue } from 'react-hook-form';
import { scorecardQType } from 'utils/typedEnums';
import { FeedbackQuestion, ScorecardRequestAnswer } from '../types/scorecardTypes';

export const formatDateWithTime = (date: string): string => {
  return moment(date).format('M/D/YY [at] h:mma');
};

export const getCompletionText = (isComplete: boolean, date: string): string => {
  const formattedDate = formatDateWithTime(date);
  return isComplete ? `Completed on ${formattedDate}` : `Started on ${formattedDate}`;
};

export const getConnectCandidateRowActionText = (
  actionKey: IndividualHiringRequestAction,
  actionIsMissing: boolean
) => {
  if (actionIsMissing) {
    return `Awaiting ${actionKey}`;
  } else {
    if (actionKey === ConnectHiringRequestAction.scorecard) {
      return 'Scorecard completed';
    } else if (actionKey === ConnectHiringRequestAction.reference) {
      return 'Completed reference check';
    } else if (actionKey === ConnectHiringRequestAction.application) {
      return 'Application submitted';
    } else {
      return 'Something went wrong';
    }
  }
};

export const getFinalRecommendationData = (
  finalRecommendation: ReadableFinalRecommendation
): FinalRecommendationDataReturned => {
  if (
    finalRecommendation === CandidateFeedbackFinalRecommendation.MAYBE ||
    finalRecommendation === LongCandidateFeedbackFinalRecommendation.MAYBE
  ) {
    return {
      alertType: 'warning',
      text: 'Maybe recommend',
    };
  } else if (
    finalRecommendation === CandidateFeedbackFinalRecommendation.YES ||
    finalRecommendation === LongCandidateFeedbackFinalRecommendation.YES
  ) {
    return {
      alertType: 'success',
      text: 'Recommended',
    };
  } else if (
    finalRecommendation === CandidateFeedbackFinalRecommendation.NO ||
    finalRecommendation === LongCandidateFeedbackFinalRecommendation.NO
  ) {
    return {
      alertType: 'error',
      text: 'Do not recommend',
    };
  } else {
    return {
      alertType: 'error',
      text: 'Something went wrong',
    };
  }
};

export function useJobStatusToGetStylingForHiringRequests(
  activeStatus: ActiveStatus,
  status: JobStatusByNumber
) {
  const theme = useTheme();

  switch (status) {
    case roleStatusByValue.archived:
      return {
        textColor: theme.palette.warning.main,
      };
    case roleStatusByValue.active:
      return activeStatus === activeStatusByValue.activeOpen
        ? {
            textColor: theme.palette.primary.main,
          }
        : {
            textColor: theme.palette.error.main,
          };
    default:
      return {
        textColor: theme.palette.text.tertiary,
      };
  }
}

export const assignEditableScorecardProperties = (
  scorecard: HiringRequestScorecard,
  setValue: UseFormSetValue<FieldValues>
): void => {
  if (!scorecard) return;

  setValue('custom_scorecard_id', scorecard.template.id);

  Object.entries(scorecard).forEach(([key, value]) => {
    if (value !== null && typeof value !== 'object') {
      setValue(key, value);
    }
  });

  setValue(
    'answers',
    scorecard.answers.map((answer) => {
      const { attachment, answer_rubric, answer_text, answer_rating, question, mc_answer } = answer;
      const { id, question_type, is_required } = question;

      const baseAnswer = {
        question_id: id,
        question_type,
        is_required,
        question,
      };

      const nonNullProperties = {
        ...(attachment !== null && { attachment }),
        ...(answer_rubric !== null && { answer_rubric }),
        ...(answer_text !== null && { answer_text }),
        ...(answer_rating !== null && { answer_rating }),
        ...(mc_answer !== null && { mc_answer }),
      };

      return {
        ...baseAnswer,
        ...nonNullProperties,
      };
    })
  );
};

export const assignQuestionProperties = (
  questions: FeedbackQuestion[],
  setValue: UseFormSetValue<FieldValues>
): void => {
  if (!questions) return;

  setValue(
    'answers',
    questions.map(({ id, question_type, is_required }) => {
      const baseAnswer = {
        question_id: id,
        question_type,
        is_required: is_required,
      };

      switch (question_type) {
        case scorecardQType.rating:
          return { ...baseAnswer, answer_rating: null };
        case scorecardQType.rubric:
          return { ...baseAnswer, answer_rubric: null };
        case scorecardQType.multiple_choice:
          return { ...baseAnswer, mc_answer: [] };
        case scorecardQType.text:
        case scorecardQType.nimble:
          return { ...baseAnswer, answer_text: '' };
        case scorecardQType.attachment:
          return { ...baseAnswer, attachment: null };
        default:
          return baseAnswer;
      }
    })
  );
};

export const assignTopLevelScorecardProperties = (
  questions: FeedbackQuestion[],
  setValue: UseFormSetValue<FieldValues>
): void => {
  return questions.forEach((question) => {
    if (
      question.question_type !== scorecardQType.final_recommendation &&
      question.question_type !== scorecardQType.cumulative_score
    ) {
      return;
    }

    switch (question.question_type) {
      case scorecardQType.final_recommendation:
        setValue('final_recommendation', null);
        setValue('include_final_recommendation', true);
        setValue('include_maybe_option', question.include_maybe_option);
        setValue('show_answer_on_preview', question.show_answer_on_preview);
        break;
      case scorecardQType.cumulative_score:
        setValue('cumulative_score', 0);
        setValue('include_cumulative_score', true);
        setValue('include_rating_questions', question.include_rating_questions);
        setValue('include_rubric_questions', question.include_rubric_questions);
        setValue('max_cumulative_score', 100); // hardcoded for now until endpoint is updated
        setValue('show_total_on_preview', question.show_total_on_preview);
        break;
      default:
        break;
    }
  });
};

export const useGetCumulativeScore = () => {
  const { setValue, watch } = useFormContext();
  const shouldShowRatingQuestions = watch('include_rating_questions');
  const shouldShowRubricQuestions = watch('include_rubric_questions');

  const updateScore = () => {
    const answers: ScorecardRequestAnswer[] = watch('answers');
    let cumulativeScore = 0;

    answers.forEach((answer) => {
      if (shouldShowRatingQuestions && typeof answer.answer_rating === 'number') {
        cumulativeScore += answer.answer_rating;
      }
      if (shouldShowRubricQuestions && typeof answer.answer_rubric === 'number') {
        cumulativeScore += answer.answer_rubric;
      }
    });

    setValue('cumulative_score', cumulativeScore);
    return cumulativeScore;
  };

  return updateScore;
};
